import React from 'react';
import type { FC } from 'react';
import { match, isType } from 'variant';
import { Trans, useTranslation } from '@wix/cli-i18n';
import {
  Alert,
  ConfirmInput,
  Box,
  Badge,
  Spinner,
  Link,
  useAsync,
  useAsyncCallback,
} from '@wix/cli-ui-kit';
import { ErrorViewer } from '@wix/cli-error-reporting';
import {
  DeploymentStatus,
  useDocumentManagementClient,
} from '@wix/document-management-client';
import { useCreatePreview } from '../PreviewCommand/useCreatePreview.js';

interface Props {
  approvePreview: boolean;
  force: boolean;
}

export const PublishCommand: FC<Props> = ({ approvePreview, force }) => {
  const { t } = useTranslation();
  const createPreview = useCreatePreview();
  const { publishDeployment } = useDocumentManagementClient();

  const publishDeploymentAction = useAsyncCallback(
    async (_, deploymentId: string) => publishDeployment(deploymentId)
  );

  const { status } = useAsync(async () => {
    const preview = await createPreview();

    const preApprovePreview =
      force ||
      (isType(preview.deploymentStatus, DeploymentStatus.Success)
        ? approvePreview
        : false);

    if (preApprovePreview) {
      void publishDeploymentAction.execute(preview.deploymentId);
    }

    return {
      ...preview,
      preApprovePreview,
    };
  }, []);

  return match(status, {
    Error: () => null,
    Loading: () => (
      <Spinner text={<Trans i18nKey="publish_command.preview.loading" />} />
    ),
    Success: ({ result }) => {
      return (
        <>
          {match(result.deploymentStatus, {
            Success: () => (
              <Alert type="success">
                <Trans
                  i18nKey="publish_command.preview.success"
                  components={[<Link url={result.previewUrl} />]}
                />
              </Alert>
            ),
            Error: ({ deploymentError }) => (
              <>
                <ErrorViewer error={deploymentError} />
                <Box marginTop={1}>
                  <Alert type="warning">
                    <Trans i18nKey="publish_command.preview.warning" />
                  </Alert>
                </Box>
              </>
            ),
          })}
          {!result.preApprovePreview && (
            <Box marginTop={1}>
              <ConfirmInput
                label={t('publish_command.confirm_publish')}
                onSubmit={(value) => {
                  if (value) {
                    void publishDeploymentAction.execute(result.deploymentId);
                  }
                }}
              />
            </Box>
          )}
          {match(publishDeploymentAction.status, {
            NotRequested: () => null,
            Error: () => null,
            Loading: () => (
              <Box marginTop={1}>
                <Spinner
                  text={<Trans i18nKey="publish_command.publish.loading" />}
                />
              </Box>
            ),
            Success: () => (
              <Box marginTop={1}>
                <Alert type="success">
                  <Badge skin="success">
                    <Trans i18nKey="general.success" />
                  </Badge>{' '}
                  <Trans
                    i18nKey="publish_command.publish.success"
                    components={[<Link url={result.siteUrl} />]}
                  />
                </Alert>
              </Box>
            ),
          })}
        </>
      );
    },
  });
};
